<template>
  <v-main>
    <!--
      `fill-height` va en el contenedor Y en la fila. En Vuetify 3 la clase
      aplicaba además `display:flex; align-items:center` sobre v-container, así
      que bastaba con ponerla ahí; en V4 sólo aplica `height:100%`, con lo que la
      fila no se estiraba y `align="center"` no tenía contra qué centrar: la
      tarjeta quedaba pegada al borde superior.
    -->
    <v-container fluid class="fill-height">
      <v-row align="center" justify="center" class="fill-height">
        <v-col cols="12" sm="8" md="6" lg="5" xl="3">
          <v-card class="elevation-12">
            <v-toolbar color="white" height="350" flat>
              <v-row>
                <v-col cols="12">
                  <v-img height="80" :src="logo" />
                </v-col>
                <v-col cols="12" class="text-center text-grey-darken-3">
                  <h2>Iniciar Sesión</h2>
                </v-col>
              </v-row>
            </v-toolbar>

            <!--
              v-skeleton-loader en Vuetify 3+ ya no envuelve un slot por
              defecto según :loading — siempre pinta el esqueleto. De ahí el
              v-if/v-else en vez de anidar.
            -->
            <v-skeleton-loader v-if="!ready" type="list-item-avatar-two-line" />
            <div v-else-if="session">
              <v-list-item class="py-1 pt-2" @click="toGateway">
                <template #prepend>
                  <v-avatar color="green">
                    <span class="text-white text-h5">{{ sessionInitial }}</span>
                  </v-avatar>
                </template>
                <v-list-item-title>
                  Ingresar como
                  <span class="text-blue">{{ sessionName }}</span>
                </v-list-item-title>
                <v-list-item-subtitle v-if="lastAccess">
                  Último acceso: {{ lastAccess }}
                </v-list-item-subtitle>
                <template #append>
                  <v-icon color="error" @click.stop="removeAccount">mdi-close</v-icon>
                </template>
              </v-list-item>
            </div>
            <div v-else><br /></div>

            <v-divider />

            <v-skeleton-loader v-if="!ready" type="list-item-avatar-two-line" />
            <v-list-item v-else class="py-1" @click="login">
              <template #prepend>
                <v-avatar color="indigo">
                  <v-icon color="white">mdi-account-circle</v-icon>
                </v-avatar>
              </template>
              <v-list-item-title>
                Ingresar con <span v-if="session">otra</span> cuenta institucional
              </v-list-item-title>
            </v-list-item>

            <v-divider />

            <v-card-actions>
              <v-spacer />
              <v-btn variant="text" size="x-small">Ayuda</v-btn>
              <v-btn variant="text" size="x-small">Acerca de</v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-main>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import type { User as OidcUser } from "oidc-client-ts";
import logo from "@/assets/img/logo.png";
import { useAuthStore } from "@/stores/auth";
import { useNotificationsStore } from "@/stores/notifications";

const route = useRoute();
const router = useRouter();
const auth = useAuthStore();
const notifications = useNotificationsStore();

const ready = ref(false);
const session = ref<OidcUser | null>(null);

// Los claims del token son `unknown` en oidc-client-ts v3, y `name` puede no
// venir. La versión anterior hacía `user.profile.name[0]` sin proteger.
const sessionName = computed(() => {
  const name = session.value?.profile?.name;
  return typeof name === "string" ? name : "";
});
const sessionInitial = computed(() => sessionName.value[0] ?? "?");
const lastAccess = computed(() => {
  const authTime = session.value?.profile?.auth_time;
  return typeof authTime === "number"
    ? new Date(authTime * 1000).toLocaleString("es-CL")
    : null;
});

function login() {
  // prompt=login fuerza la pantalla de Keycloak para poder cambiar de cuenta.
  auth.login({ prompt: "login" });
}

function toGateway() {
  router.push({ path: "/gateway" });
}

async function removeAccount() {
  await auth.logout();
}

onMounted(async () => {
  if (route.query.notify === "logout") {
    notifications.success("Se ha cerrado la sesión exitosamente.", "Autenticación");
  } else if (route.query.notify === "error") {
    notifications.error(
      "No fue posible iniciar la sesión. Si el problema persiste comuníquese con el administrador de la plataforma.",
      "Autenticación"
    );
  }

  // La versión anterior hacía `await auth.removeIdentity()` acá, borrando la
  // sesión del storage antes de intentar el SSO silencioso. Eso convierte
  // cualquier rebote transitorio a esta pantalla en un cierre de sesión real, y
  // no hacía falta: el botón de login usa `prompt: "login"`, que ya fuerza el
  // selector de cuenta en Keycloak.
  try {
    session.value = (await auth.getIdentity()) ?? (await auth.silentLogin());
  } catch {
    // Esperado cuando no hay sesión en el IDP. No es un error que reportar:
    // la versión anterior lo mandaba a console.log y sólo generaba ruido.
    session.value = null;
  } finally {
    ready.value = true;
  }
});
</script>
